/* Copyright (c) 2020-2023 Nordcloud Oy or its affiliates. All Rights Reserved. */

import { useCallback, useMemo, useState } from "react";
import { Box, Button, ModalConfirm, theme } from "@nordcloud/gnui";
import {
  useGetUsersPermissionsQuery,
  useRemoveUserPermissionsMutation,
} from "~/generated/graphql";
import {
  BreadcrumbsBox,
  ErrorWrap,
  GlobalSpinner,
  LoaderWrap,
  Pagination,
  ReactTable,
  TableLoader,
} from "~/components";
import { ERROR_TEXT } from "~/constants";
import { useDisclosure, useQueryState } from "~/hooks";
import { useCustomers } from "~/services/customers";
import { showError } from "~/services/toast";
import { generateActionConfirmText } from "~/tools";
import { SearchSortContainer } from "./components";
import { PageName, SELECT_OPTIONS_USERS_PERMISSIONS } from "./constants";
import { useSettingsSort } from "./hook";
import { Account, QuerySort } from "./types";
import { AddUserPermissionsSidebar } from "./UsersAndPermissions/AddUserPermissionsSidebar";
import { UsersAndPermissionsColumns } from "./UsersAndPermissions/UsersAndPermissionsListColumns";

export function UsersAndPermissionsPage() {
  const { isAdminLite } = useCustomers();
  const {
    isOpen: isModalConfirmOpen,
    close: closeModalConfirm,
    open: openModalConfirm,
  } = useDisclosure();
  const [userEmail, setUserEmail] = useState("");
  const [editableData, setEditableData] = useState<Account | null>(null);

  const { isOpen, close, open } = useDisclosure();

  const { state, updateQueryState } = useQueryState();
  // @ts-expect-error ignored temporary after migration to TypeScript 4.9
  const { page = 0, limit = 20, field, order, query }: QuerySort = state;

  const [searchInput, setSearchInput] = useState(query);
  const { sortField, sortDirection, onSortSelect, onSortOrderChange } =
    useSettingsSort({
      field,
      order,
      page,
      pageName: PageName.UsersAndPermissions,
      // @ts-expect-error ignored temporary after migration to TypeScript 4.9
      updateQueryState,
    });

  const [removeUserPermissions, { loading: isRemoveUserPermissionsLoading }] =
    useRemoveUserPermissionsMutation();
  const { data, loading, refetch, error } = useGetUsersPermissionsQuery({
    variables: {
      limit,
      page,
      sortByValue: field,
      sortByOrder: order,
      searchByValue: searchInput,
    },
  });

  const onRemoveConfirmationModal = useCallback(
    (email: string) => {
      setUserEmail(email);
      openModalConfirm();
    },
    [setUserEmail, openModalConfirm]
  );

  const onRemoveUserPermissions = async (email: string) => {
    try {
      await removeUserPermissions({
        variables: {
          email,
        },
      });
      await refetch();
    } catch {
      showError(ERROR_TEXT.default);
    }
    closeModalConfirm();
    setUserEmail("");
  };

  const onAddNewUser = () => {
    setEditableData(null);
    open();
  };

  const handleChange = async () => {
    await refetch();
    close();
  };

  const onEdit = useCallback(
    (item: Account) => {
      setEditableData(item);
      open();
    },
    [setEditableData, open]
  );

  const usersTableColumns = useMemo(() => {
    const adminLiteExcludedAccessors = [
      "isOrgAdminFromPanel",
      "access.applications",
      "access.role",
    ];
    return UsersAndPermissionsColumns(
      onRemoveConfirmationModal,
      isAdminLite ? undefined : onEdit
    ).filter((column) =>
      isAdminLite ? !adminLiteExcludedAccessors.includes(column.accessor) : true
    );
  }, [isAdminLite, onEdit, onRemoveConfirmationModal]);

  return (
    <>
      <GlobalSpinner loading={isRemoveUserPermissionsLoading} />
      <BreadcrumbsBox
        title="Users and Permissions"
        labels={[
          {
            value: "users-and-permissions",
            label: "Users and Permissions",
          },
        ]}
      >
        <Button
          style={{ marginBottom: theme.spacing.spacing01 }}
          icon="plus"
          onClick={onAddNewUser}
        >
          Add new User
        </Button>
      </BreadcrumbsBox>
      <SearchSortContainer
        placeHolder="e.g., Email"
        sortOptions={SELECT_OPTIONS_USERS_PERMISSIONS}
        sortField={sortField}
        sortDirection={sortDirection}
        searchInput={(searchInput ?? "").toString()}
        setSearchInput={setSearchInput}
        onSortSelect={onSortSelect}
        onSortOrderChange={onSortOrderChange}
      />
      <ErrorWrap error={error}>
        <Box spacing="spacing04">
          <LoaderWrap loading={loading} Component={Loader}>
            <ReactTable
              striped
              columns={usersTableColumns}
              sort={false}
              data={data?.usersPermissions?.usersPermissions ?? []}
            />
            <Pagination count={data?.usersPermissions?.count ?? 0} />
          </LoaderWrap>
        </Box>
      </ErrorWrap>
      <AddUserPermissionsSidebar
        isOpen={isOpen}
        editableData={editableData}
        onChange={handleChange}
        onClose={close}
      />
      <ModalConfirm
        isOpen={isModalConfirmOpen}
        confirm={() => onRemoveUserPermissions(userEmail)}
        actionLabel="Delete"
        contentLabel="Delete Permissions"
        onClose={closeModalConfirm}
      >
        {generateActionConfirmText(`permissions for ${userEmail}`)("delete")()}
      </ModalConfirm>
    </>
  );
}

function Loader() {
  return (
    <TableLoader
      gapY={10}
      gapX={10}
      css={{
        marginTop: theme.spacing.spacing04,
      }}
      rows={[
        {
          height: 20,
          count: 1,
          items: [
            {
              widthPercent: 3,
            },
            {
              widthPercent: 41,
            },
            {
              widthPercent: 15,
            },
            {
              widthPercent: 35,
            },
            {
              widthPercent: 6,
            },
          ],
        },
        {
          height: 60,
          count: 9,
          items: [
            {
              widthPercent: 3,
            },
            {
              widthPercent: 41,
            },
            {
              widthPercent: 15,
            },
            {
              widthPercent: 35,
            },
            {
              widthPercent: 6,
            },
          ],
        },
      ]}
    />
  );
}
